import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Skeleton,
  Typography,
  styled,
} from "@mui/material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { CustomButton } from "@/components/custom/custom-button";
import { FormSelectInput } from "@/components/custom/custom-form-select-input";
import { useRoutingConfig } from "@/hooks/use-routing-config";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { updateProfileSchema } from "@/schemas/routing.schema";
import { updateRoutingProfile } from "@/services/tech.service";
import { FormCheckList } from "@/components/custom/custom-form-check-list";
import { CustomFormSwitch } from "@/components/custom/custom-form-switch";
import { showToast } from "@/redux/actions/toast-actions";
import { useDispatch } from "react-redux";

const StyledDialog = styled(Dialog)(() => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    maxWidth: 540,
    width: "100%",
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: "10px",
  backgroundColor: theme.palette.primary.main + "18",
  flexShrink: 0,
}));

const UpdateRoutingProfileDialog = ({ open, onClose, profile }) => {
  const queryClient = useQueryClient();
    const dispatch = useDispatch();

  const { data: configData, isLoading: configLoading } = useRoutingConfig(open);

  const config = configData?.data ?? {};

  const formik = useFormik({
    initialValues: {
      precedence: profile?.precedence ?? "",
      selectionStrategy: profile?.selectionStrategy ?? "ALL",
      routingTypes: profile?.routingTypes ?? [],
      cardTypes: profile?.cardTypes ?? [],
      processors: profile?.processors ?? [],
      isActive: profile?.isActive ?? true,
      forcePrecedence: profile?.forcePrecedence ?? false,
    },
    enableReinitialize: true,
    validationSchema: updateProfileSchema,
    onSubmit: (values) => {
      mutation.mutate({
        id: profile?.id,
        payload: {
          precedence: Number(values.precedence),
          selectionStrategy: values.selectionStrategy,
          routingTypes: values.routingTypes,
          cardTypes: values.cardTypes,
          processors: values.processors,
          isActive: values.isActive,
          forcePrecedence: values.forcePrecedence,
        },
      });
    },
  });

  const mutation = useMutation({
    mutationFn: ({ id, payload }) => updateRoutingProfile(id, payload),
    onSuccess: (data) => {
      dispatch(
        showToast(data?.message ?? "Profile updated successfully", "success"),
      );
      queryClient.invalidateQueries({ queryKey: ["routing-profiles"] });
      onClose();
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    mutation.reset();
    formik.resetForm();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent sx={{ pt: 2.5, px: 2.5, pb: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <IconWrapper>
            <EditNoteIcon sx={{ color: "primary.main", fontSize: 22 }} />
          </IconWrapper>
          <Box>
            <DialogTitle sx={{ p: 0, fontSize: "1rem", fontWeight: 600 }}>
              Update Routing Profile
            </DialogTitle>
            <Typography variant="caption" color="text.secondary">
              Modify the existing profile configuration.
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <FormTextInput
                formik={formik}
                name="precedence"
                label="Precedence"
                type="number"
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormSelectInput
                formik={formik}
                name="selectionStrategy"
                label="Selection Strategy"
                options={[
                  { value: "ALL", label: "ALL" },
                  { value: "ONE", label: "ONE" },
                ]}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 3 }}>
            <CustomFormSwitch
              formik={formik}
              name="isActive"
              label="Active"
              disabled={mutation.isPending}
            />
            <CustomFormSwitch
              formik={formik}
              name="forcePrecedence"
              label="Force Precedence"
              disabled={mutation.isPending}
            />
          </Box>

          <Divider />

          {configLoading ? (
            <ChecklistSkeleton label="Routing Types" />
          ) : (
            <FormCheckList
              formik={formik}
              name="routingTypes"
              label="Routing Types"
              options={config.routingTypes ?? []}
            />
          )}

          <Divider />

          {configLoading ? (
            <ChecklistSkeleton label="Card Types" />
          ) : (
            <FormCheckList
              formik={formik}
              name="cardTypes"
              label="Card Types"
              options={config.cardTypes ?? []}
            />
          )}

          <Divider />

          {configLoading ? (
            <ChecklistSkeleton label="Processors" />
          ) : (
            <FormCheckList
              formik={formik}
              name="processors"
              label="Processors"
              options={config.processors ?? []}
            />
          )}
        </Box>

        {mutation.isError && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {mutation.error?.message ?? "Failed to update profile. Try again."}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={mutation.isPending}
        >
          Cancel
        </CustomButton>

        <CustomButton
          size="small"
          onClick={formik.handleSubmit}
          disabled={mutation.isPending || !formik.dirty}
          sx={{ minWidth: 130 }}
        >
          {mutation.isPending ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={14} sx={{ color: "#fff" }} />
              Updating...
            </Box>
          ) : (
            "Save Changes"
          )}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

const ChecklistSkeleton = ({ label }) => (
  <Box>
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        mb: 0.75,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
      {[1, 2, 3].map((i) => (
        <Skeleton
          key={i}
          variant="rounded"
          width={90}
          height={48}
          sx={{ borderRadius: "8px" }}
        />
      ))}
    </Box>
  </Box>
);

export default UpdateRoutingProfileDialog;
